export type PersistentWalletLedgerEntry = {
  id?: string;
  transaction_id?: string;
  entry_type?: string;
  debit_micros?: number;
  credit_micros?: number;
  reference_type?: string;
  reference_id?: string;
  metadata_json?: string;
  created_at?: string;
  [key: string]: unknown;
};

const DEFAULT_MAX_LEDGER_ENTRIES = 240;

function stringValue(value: unknown) {
  return String(value ?? "").trim();
}

function lowerValue(value: unknown) {
  return stringValue(value).toLowerCase();
}

function ledgerMetadata(entry: PersistentWalletLedgerEntry): Record<string, unknown> {
  const raw = entry.metadata_json;
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

function firstMetadataValue(metadata: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = stringValue(metadata[key]);
    if (value) return value;
  }
  return "";
}

function addKey(keys: Set<string>, value: unknown) {
  const text = stringValue(value);
  if (text) keys.add(text);
}

function ledgerSortMs(entry: PersistentWalletLedgerEntry) {
  const ms = new Date(stringValue(entry.created_at)).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

export function walletLedgerEntryJobKeys(entry: PersistentWalletLedgerEntry) {
  const keys = new Set<string>();
  const metadata = ledgerMetadata(entry);
  const referenceType = lowerValue(entry.reference_type);
  const referenceID = stringValue(entry.reference_id);
  if (["provider_job", "wallet_provider_job", "genrl_provider_job"].includes(referenceType) && referenceID) {
    keys.add(referenceID);
  }
  for (const key of [
    "provider_job_id",
    "job_id",
    "genrl_job_id",
    "client_launch_id",
    "runtime_session_id",
  ]) {
    addKey(keys, metadata[key]);
  }
  return keys;
}

export function walletLedgerEntryJobKey(entry: PersistentWalletLedgerEntry) {
  return firstMetadataValue(ledgerMetadata(entry), [
    "provider_job_id",
    "job_id",
    "genrl_job_id",
    "client_launch_id",
    "runtime_session_id",
  ]) || Array.from(walletLedgerEntryJobKeys(entry))[0] || "";
}

export function isOptimisticWalletLedgerEntry(entry: PersistentWalletLedgerEntry) {
  const type = lowerValue(entry.entry_type);
  const id = lowerValue(entry.id);
  return type === "job_pending" || type === "wallet_provider_job_pending" || id.startsWith("job:");
}

export function walletLedgerEntryStableKey(entry: PersistentWalletLedgerEntry) {
  const id = stringValue(entry.id);
  if (id) return `id:${id}`;

  const transactionID = stringValue(entry.transaction_id);
  if (transactionID) {
    return [
      "tx",
      transactionID,
      lowerValue(entry.entry_type),
      lowerValue(entry.reference_type),
      stringValue(entry.reference_id),
    ].join(":");
  }

  const jobKey = walletLedgerEntryJobKey(entry);
  if (jobKey && isOptimisticWalletLedgerEntry(entry)) return `optimistic-job:${jobKey}`;

  const referenceType = lowerValue(entry.reference_type);
  const referenceID = stringValue(entry.reference_id);
  const entryType = lowerValue(entry.entry_type);
  if (referenceType && referenceID && entryType) return `ref:${referenceType}:${referenceID}:${entryType}`;

  return [
    "fingerprint",
    entryType,
    referenceType,
    referenceID,
    stringValue(entry.created_at),
    Number(entry.credit_micros || 0),
    Number(entry.debit_micros || 0),
  ].join(":");
}

export function mergePersistentWalletLedgerEntries<T extends PersistentWalletLedgerEntry>(
  previous: T[],
  incoming: T[],
  maxEntries = DEFAULT_MAX_LEDGER_ENTRIES,
) {
  const realJobKeys = new Set<string>();
  [...previous, ...incoming].forEach((entry) => {
    if (isOptimisticWalletLedgerEntry(entry)) return;
    for (const jobKey of walletLedgerEntryJobKeys(entry)) {
      realJobKeys.add(jobKey);
    }
  });

  const merged = new Map<string, T>();
  const add = (entry: T) => {
    if (isOptimisticWalletLedgerEntry(entry)) {
      for (const jobKey of walletLedgerEntryJobKeys(entry)) {
        if (realJobKeys.has(jobKey)) return;
      }
    }
    merged.set(walletLedgerEntryStableKey(entry), entry);
  };

  previous.forEach(add);
  incoming.forEach(add);

  return Array.from(merged.values())
    .sort((a, b) => {
      const sortDelta = ledgerSortMs(b) - ledgerSortMs(a);
      if (sortDelta !== 0) return sortDelta;
      return walletLedgerEntryStableKey(b).localeCompare(walletLedgerEntryStableKey(a));
    })
    .slice(0, Math.max(1, maxEntries));
}
